import { Box, Grid, Tooltip, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-light-svg-icons';
import React from 'react';
import { formatDateIgnoreTz } from '../../../../shared/utils';
import {
  dimGrey,
  lightBackground1,
  lightBlack,
  lightGrey,
  outlineSeparator,
} from '../../../../mainTheme';
import {
  MedicationStat,
  MedicationStats,
} from '../../../../store/hubOwner/types';

interface MedicationStatsCardsProps {
  stats?: MedicationStats | null;
}

type CardConfig = {
  key: keyof MedicationStats;
  label: string;
  isDate?: boolean;
};

const CARDS: CardConfig[] = [
  { key: 'total_prescribed_meds', label: 'Total prescribed meds' },
  { key: 'pouched_meds', label: 'Pouched meds' },
  { key: 'non_pouched_meds', label: 'Non-pouched meds' },
  { key: 'med_events_remaining', label: 'Med events remaining' },
  { key: 'days_remaining', label: 'Days remaining' },
  { key: 'med_roll_end_date', label: 'Med roll end date', isDate: true },
];

const formatValue = (stat: MedicationStat, isDate?: boolean): string => {
  if (isDate) {
    return formatDateIgnoreTz(stat.datetime, 'MMM d, yyyy');
  }
  return stat.count == null ? 'N/A' : String(stat.count);
};

const MedicationStatsCards: React.FC<MedicationStatsCardsProps> = ({
  stats,
}) => {
  if (!stats || Object.keys(stats).length === 0) {
    return null;
  }

  return (
    <Grid container spacing={2.5} mb={3}>
      {CARDS.map(({ key, label, isDate }) => {
        const stat = stats[key];
        if (!stat) {
          return null;
        }
        return (
          <Grid item xs={6} sm={4} lg={2} key={key}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 2.5,
                p: 1.5,
                height: '100%',
                borderRadius: '8px',
                backgroundColor: lightBackground1,
                border: `1px solid ${outlineSeparator}`,
              }}
            >
              <Box display="flex" flexDirection="column" gap={0.5}>
                <Typography
                  sx={{
                    color: lightBlack,
                    fontSize: '12px',
                    fontWeight: 700,
                    lineHeight: '16px',
                  }}
                >
                  {label}
                </Typography>
                <Typography
                  sx={{
                    color: dimGrey,
                    fontSize: '12px',
                    fontWeight: 500,
                    lineHeight: '16px',
                  }}
                >
                  {formatValue(stat, isDate)}
                </Typography>
              </Box>
              {stat.description && (
                <Tooltip
                  title={stat.description}
                  placement="bottom-end"
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: lightBackground1,
                        color: dimGrey,
                        border: `1px solid ${outlineSeparator}`,
                        borderRadius: '8px',
                        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                        p: 2,
                        maxWidth: '18.75rem',
                        fontSize: '12px',
                        fontWeight: 500,
                        lineHeight: '16px',
                      },
                    },
                    arrow: { sx: { color: lightBackground1 } },
                  }}
                >
                  <Box
                    component="span"
                    aria-label={`${label} info`}
                    sx={{ display: 'flex', cursor: 'pointer' }}
                  >
                    <FontAwesomeIcon
                      icon={faCircleInfo}
                      color={lightGrey}
                      fontSize={12}
                    />
                  </Box>
                </Tooltip>
              )}
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default MedicationStatsCards;
